/** Spotify / Apple Music link → önizleme (misafir tarayıcısı; doğrudan API). */

export type SongLinkPlatform = "spotify" | "appleMusic";

export type SongPreviewResolved = {
  platform: SongLinkPlatform;
  trackUrl: string;
  trackTitle: string;
  /** Firestore uyumu için alan korunur; arayüzde gösterilmez. */
  artistName: string;
  thumbnailUrl: string;
};

/** `intl-tr`, `intl-pt-br` vb. bölümlü paylaşım yolları. */
const SPOTIFY_TRACK_OR_EPISODE =
  /open\.spotify\.com\/(?:intl-[a-z]{2}(?:-[a-z]{2,4})?\/)?(track|episode)\/([a-zA-Z0-9]+)/i;
const SPOTIFY_SHORT = /spotify\.link\/[a-zA-Z0-9_-]+/i;

export function detectSongLinkPlatform(raw: string): SongLinkPlatform | null {
  const s = raw.trim();
  if (!s) return null;
  if (/spotify\.com|spotify\.link/i.test(s)) return "spotify";
  if (/music\.apple\.com|itunes\.apple\.com/i.test(s)) return "appleMusic";
  return null;
}

export function normalizeSpotifyShareUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  if (SPOTIFY_SHORT.test(t)) {
    /* Kısa linkler çözülmeden oEmbed bazen dönmez; yine de tam URL göndermeyi deneriz. */
    return t.startsWith("http") ? t : `https://${t.replace(/^\/\//, "")}`;
  }
  const m = t.match(SPOTIFY_TRACK_OR_EPISODE);
  if (m) {
    const kind = m[1].toLowerCase();
    const id = m[2];
    return `https://open.spotify.com/${kind}/${id}`;
  }
  if (/open\.spotify\.com/i.test(t)) {
    try {
      const u = new URL(t.startsWith("http") ? t : `https://${t}`);
      if (/open\.spotify\.com$/i.test(u.hostname)) return u.toString();
    } catch {
      /* geçersiz URL */
    }
  }
  return null;
}

const APPLE_HOST_RE = /^(?:www\.)?(?:music|itunes)\.apple\.com$/i;

const APPLE_PATH_TYPES = new Set(["album", "song", "playlist", "music-video", "post"]);

/** @deprecated Dışarıda kullanılıyorsa; ülke bilgisi olmadan lookup eksik kalabilir. */
export function extractAppleMusicCatalogId(raw: string): string | null {
  return extractAppleMusicLookup(raw)?.catalogId ?? null;
}

function extractTrailingAppleNumericId(pathParts: string[]): string | null {
  if (pathParts.length < 2) return null;
  const t = pathParts[0]?.toLowerCase();
  if (!t || !APPLE_PATH_TYPES.has(t)) return null;
  const last = pathParts[pathParts.length - 1] ?? "";
  if (/^\d{5,}$/.test(last)) return last;
  const idPrefix = last.match(/^id(\d{5,})$/i);
  return idPrefix?.[1] ?? null;
}

/**
 * Apple Music / iTunes paylaşım URL’sinden katalog id + storefront (ülke kodu).
 * Örn. `music.apple.com/tr/album/…/499333901` → country tr, albüm lookup’ı doğru mağazadan gelir.
 */
export function extractAppleMusicLookup(raw: string): {
  catalogId: string;
  country: string;
  pageUrl: string;
} | null {
  const t = raw.trim();
  if (!t) return null;
  const withScheme = t.startsWith("http") ? t : `https://${t}`;
  try {
    const u = new URL(withScheme);
    if (!APPLE_HOST_RE.test(u.hostname)) return null;

    const parts = u.pathname.split("/").filter(Boolean);
    let country = "us";
    let tailParts = parts;

    if (
      parts.length >= 4 &&
      /^[a-z]{2}$/i.test(parts[0] ?? "") &&
      APPLE_PATH_TYPES.has((parts[1] ?? "").toLowerCase())
    ) {
      country = (parts[0] ?? "us").toLowerCase();
      tailParts = parts.slice(1);
    }

    const qi = u.searchParams.get("i");
    if (qi && /^\d+$/.test(qi)) {
      return { catalogId: qi, country, pageUrl: withScheme };
    }

    const fromSegments = extractTrailingAppleNumericId(tailParts);
    if (fromSegments) {
      return { catalogId: fromSegments, country, pageUrl: withScheme };
    }

    const idInPath = u.pathname.match(/\/id(\d{5,})(?:\/|$|\?)/i);
    if (idInPath?.[1]) {
      return { catalogId: idInPath[1], country, pageUrl: withScheme };
    }
  } catch {
    /* geçersiz URL */
  }
  const qFallback = t.match(/[?&]i=(\d{5,})/);
  if (qFallback) {
    return {
      catalogId: qFallback[1],
      country: "us",
      pageUrl: t.startsWith("http") ? t : `https://${t}`,
    };
  }
  return null;
}

type SpotifyOembedJson = {
  title?: string;
  thumbnail_url?: string;
};

type ItunesLookupEntity = {
  wrapperType?: string;
  kind?: string;
  trackName?: string;
  collectionName?: string;
  artworkUrl100?: string;
};

type AppleLookupJson = {
  results?: ItunesLookupEntity[];
};

function pickAppleMusicLineFromLookupResults(results: ItunesLookupEntity[] | undefined): {
  title: string;
  thumb: string;
} | null {
  if (!results?.length) return null;

  const songHit = results.find((r) => {
    const tn = (r.trackName ?? "").trim();
    return tn.length > 0;
  });
  if (songHit) {
    return {
      title: songHit.trackName!.trim(),
      thumb: (songHit.artworkUrl100 ?? "").trim(),
    };
  }

  const albumHit = results.find((r) => {
    const cn = (r.collectionName ?? "").trim();
    return cn.length > 0;
  });
  if (albumHit) {
    return {
      title: albumHit.collectionName!.trim(),
      thumb: (albumHit.artworkUrl100 ?? "").trim(),
    };
  }

  return null;
}

export async function resolveSongPreviewFromLink(raw: string): Promise<SongPreviewResolved> {
  const platform = detectSongLinkPlatform(raw);
  if (!platform) {
    throw new Error("invalid");
  }
  if (platform === "spotify") {
    const url = normalizeSpotifyShareUrl(raw);
    if (!url) throw new Error("invalid");
    /* open.spotify.com/oembed — tarayıcıda CORS *. embed.spotify.com genelde 308 + CORS sorunu çıkarır. */
    const oembed = `https://open.spotify.com/oembed?url=${encodeURIComponent(url)}&format=json`;
    const res = await fetch(oembed);
    if (!res.ok) throw new Error("fetch");
    const j = (await res.json()) as SpotifyOembedJson;
    const title = (j.title ?? "").trim();
    const thumb = (j.thumbnail_url ?? "").trim();
    if (!title) throw new Error("fetch");

    return {
      platform: "spotify",
      trackUrl: url,
      trackTitle: title,
      artistName: "",
      thumbnailUrl: thumb,
    };
  }

  const parsed = extractAppleMusicLookup(raw);
  if (!parsed) throw new Error("invalid");
  const { catalogId, country, pageUrl } = parsed;
  const lookup = `https://itunes.apple.com/lookup?id=${encodeURIComponent(catalogId)}&country=${encodeURIComponent(country)}`;
  const res = await fetch(lookup);
  if (!res.ok) throw new Error("fetch");
  const j = (await res.json()) as AppleLookupJson;
  const line = pickAppleMusicLineFromLookupResults(j.results);
  if (!line) throw new Error("fetch");
  let thumb = line.thumb;
  if (thumb.includes("100x100")) {
    thumb = thumb.replace("100x100", "300x300");
  }
  return {
    platform: "appleMusic",
    trackUrl: pageUrl,
    trackTitle: line.title,
    artistName: "",
    thumbnailUrl: thumb,
  };
}
